import CarotekColors from "@carotek/utils/Colors";
import { addPagesFromContext, addWidgetsFromContext } from "@insite/client-framework/Configuration";
import { preStyleGuideTheme, setPostStyleGuideTheme } from "@insite/client-framework/ThemeConfiguration";
import { TextAreaStyle } from "@insite/mobius/TextArea";
import { InputStyle } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";

import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";
// For production, include a set of commonly used widgets in the main bundle
// the rest will be split into their own chunks
// Do not do this for the ./Overrides folder
// const widgets = IS_PRODUCTION
//     ? require.context("./Widgets", true, /(Header|Basic|Common|Footer|SignIn)\/.+?\.tsx$/)
//     : require.context("./Widgets", true, /\.tsx$/);

const widgets = require.context("./Widgets", true, /\.tsx$/);
// enable hot module reloading for widgets
const onHotWidgetReplace = addWidgetsFromContext(widgets);
if (module.hot) {
    module.hot.accept((widgets as any).id, () => onHotWidgetReplace(require.context("./Widgets", true, /\.tsx$/)));
}

// Load all custom pages so they are included in the bundle and enable hot module reloading for them. Do not do this for the ./Overrides folder
// const pages = require.context("./Pages", true, /\.tsx$/);
// const onHotPageReplace = addPagesFromContext(pages);
// if (module.hot) {
//     module.hot.accept(pages.id, () => onHotPageReplace(require.context("./Pages", true, /\.tsx$/)));
// }

// Load all handlers so they are included in the bundle.
const handlers = require.context("./Handlers", true);
handlers.keys().forEach(key => handlers(key));

// in production, the widget extensions are included in the chunks created for widgets
// if (!IS_PRODUCTION) {
//     // load all widget extensions. They could be loaded individually instead
//     const widgetExtensions = require.context("./StyleExtensions", true);
//     widgetExtensions.keys().forEach(key => widgetExtensions(key));
// }

const widgetExtensions = require.context("./StyleExtensions", true);
widgetExtensions.keys().forEach(key => widgetExtensions(key));

// Add some post styleguide customizations. These cannot be overridden by the style guide.
setPostStyleGuideTheme({
    breakpoints: {
        keys: ["xs", "sm", "md", "lg", "xl"],
        values: [0, 576, 768, 992, 1200],
        maxWidths: [540, 540, 820, 960, 1440],
    },
    globalStyle: {
        css: css`
            main[class^="PageStyle"] {
                padding-top: 0;
            }

            small {
                font-size: 60%;
                line-height: inherit;
            }

            @media print {
                html {
                    [class^="PageLayoutWrapper"] {
                        > span[class^="TypographyStyle"] {
                            display: none;
                        }
                    }
                }

                @page {
                    size: auto;
                    margin: 0mm;
                }

                .hide-for-print {
                    display: none !important;
                }
            }
        `,
    },
    typography: {
        fontFamilyImportUrl: "/staticContent/stylesheet.css",
        body: {
            size: "1rem",
            weight: 400,
            lineHeight: "24px",
            fontFamily: "OpenSans",
        },
        p: {
            size: "14px",
            weight: 400,
            lineHeight: "1.6rem",
            fontFamily: "OpenSans",
            color: "text.main",

            css: css`
                margin-bottom: 1rem;
                span {
                    color: ${getColor("text.main")};
                }
            `,
        },
        h1: {
            size: "2.25em",
            weight: "bold",
            color: "text.main",
            lineHeight: "1.25em",
            fontFamily: "NeuronBold",
            css: css`
                margin-top: 0.75em;
                margin-bottom: 0.5em;
            `,
        },
        h2: {
            size: "1.5em",
            weight: 700,
            color: "text.accent",
            fontFamily: "NeuronBold",
            css: css`
                margin-top: 1em;
                margin-bottom: 0.75em;
                font-size: 28px;
                border-bottom: 2px solid ${getColor("primary")};
                color: ${getColor("primary")};
                display: inline-block;
                margin: 0 0 10px;
                padding-bottom: 5px;
                text-transform: uppercase;
                line-height: 26px;
            `,
        },
        h3: {
            size: "22px",
            weight: 600,
            color: "text.main",
            fontFamily: "NeuronBold",
            css: css`
                margin: 0;
            `,
        },
        h4: {
            size: "1em",
            weight: 700,
            color: "text.accent",
            lineHeight: "28px",
            fontFamily: "NeuronBold",
        },
        h5: {
            size: "1em",
            weight: 700,
            color: "text.accent",
            lineHeight: "26px",
            fontFamily: "NeuronBold",
        },
        h6: {
            size: "1em",
            weight: 700,
            color: "text.accent",
            lineHeight: "24px",
            fontFamily: "NeuronBold",
        },
        headerPrimary: {
            fontFamily: "OpenSans",
        },
        headerSecondary: {
            fontFamily: "OpenSans",
            weight: 600,
        },
        headerTertiary: {
            fontFamily: "OpenSans",
        },
    },
    link: {
        defaultProps: {
            css: css`
                :hover {
                    [class^="TypographyStyle"] {
                        color: ${getColor("text.link")};
                    }
                }
            `,
            typographyProps: {
                css: css`
                    :hover {
                        text-decoration: underline;
                    }
                `,
            },
        },
    },
    focus: {
        style: "none",
    },
    button: {
        primary: {
            typographyProps: {
                transform: "uppercase",
                size: 14,
                color: `${CarotekColors.white}`,
                fontFamily: "OpenSansBold",
            },
            hoverStyle: {
                backgroundColor: `${getColor("danger")}`,
                borderColor: `${getColor("danger")}`,
            },
            css: css`
                min-height: 37.8px;
                padding: 0 50px;
                &:hover {
                    background-color: ${getColor("danger")};
                    border-color: ${getColor("danger")};
                }
            `,
        },
        secondary: {
            typographyProps: {
                transform: "uppercase",
                size: 14,
                fontFamily: "OpenSansBold",
            },
        },
        tertiary: {
            typographyProps: {
                transform: "uppercase",
                size: 14,
                fontFamily: "OpenSansBold",
            },
        },
    },
    formField: {
        defaultProps: {
            cssOverrides: {
                inputSelect: css`
                    font-size: 14px;
                    border: 1px solid ${CarotekColors.grey1} !important;
                    height: 37.8px;
                `,
                formInputWrapper: css`
                    margin-top: 2px !important;
                    > ${InputStyle}, > ${TextAreaStyle}, select {
                        &:focus,
                        &:focus-within {
                            box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
                            border: 1px solid ${CarotekColors.grey7} !important;
                            background-color: ${CarotekColors.white} !important;
                        }
                    }
                    /* > div[class^="DateTimePickerStyle"] {
                        > div[class^="react-datetime-picker"] {
                            &:focus,
                            &:focus-within {
                                border: 2px solid ${getColor("text.main")} !important;
                            }
                        }
                    }
                    > div[class^="FileUploadStyle-sc"] {
                        &:focus-within,
                        &:focus {
                            border: 2px solid ${getColor("text.main")} !important;
                        }
                        input {
                            font-size: 1rem;
                            &:focus,
                            &:focus-within {
                                border: 2px solid ${getColor("text.main")} !important;
                            }
                        }
                    } */
                `,
            },
            labelProps: {
                color: "text.main",
                size: 14,
                weight: "normal",
                transform: "capitalize",
            },
            errorProps: {
                size: 14,
                fontFamily: "OpenSansSemiBold",
                weight: 600,
                css: css`
                    padding: 0.5em 1em;
                    line-height: 1.5em;
                    background: ${CarotekColors.white};
                    color: ${CarotekColors.red2};
                    display: block;
                    margin-bottom: 0.25em;
                    border: 1px solid ${CarotekColors.red2};
                `,
            },
        },
    },
    tab: {
        groupDefaultProps: {
            cssOverrides: {
                tabContent: css`
                    border: 1px solid ${CarotekColors.grey11};
                    position: relative;
                    font-size: 14px;
                    line-height: 1.6rem;
                    margin-bottom: 15px;
                `,
            },
        },
        defaultProps: {
            css: css`
                background-color: ${getColor("text.link")};
                padding: 6px 16px;
                margin-right: 5px;
                border-width: 0px !important;
                cursor: pointer;

                [class^="TypographyStyle"] {
                    color: ${CarotekColors.white};
                }

                &[aria-selected="true"] {
                    background-color: ${CarotekColors.white};
                    border: 1px solid ${CarotekColors.grey11} !important;
                    border-bottom: 0 !important;
                    [class^="TypographyStyle"] {
                        color: ${getColor("text.main")};
                    }
                }
            `,
            typographyProps: {
                size: 14,
            },
        },
    },
    checkbox: {
        defaultProps: {
            color: "text.link",
            css: css`
                span[role="checkbox"] {
                    &:focus {
                        border: none;
                        box-shadow: none;
                        outline: 0;
                    }
                }
            `,
        },
    },
    radio: {
        defaultProps: {
            color: "text.link",
            css: css`
                span[role="checkbox"] {
                    &:focus {
                        border: none;
                        box-shadow: none;
                        outline: 0;
                    }
                }
            `,
        },
    },
});
